import { trpcClient } from "@tokenterminal/core/utils/trpc"
import { type ProjectDetailsMeta } from "@tokenterminal/tt-types"
import { fetchProject } from "api/ApiCalls"
import { PageMeta } from "components/PageMeta"
import {
  ProjectDatasetSchema,
  ProjectOrganizationSchema,
} from "components/Schemas/ProjectSchema"
import { Tracking } from "components/Tracking"
import { isRunningBuildInPreview } from "helpers/config"
import { V3StaticLayout } from "layouts/v3Static"
import { type PageType } from "modules/analytics/types"
import OverviewTab from "modules/projects/OverviewTab/OverviewTab"
import { ProjectDetailPage } from "modules/projects/ProjectDetailPage"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import type { GetStaticPaths, GetStaticProps } from "next"

interface Props {
  name: string
  projectSlug: string
  data: ProjectDetailsMeta
}

function ProjectPage({ name, projectSlug: slug, data }: Props) {
  const router = useRouter()
  const projectSlug = (router.query.projectSlug as string | undefined) || slug
  const [title, setTitle] = useState(name)

  useEffect(() => {
    ;(async () => {
      const project = await fetchProject(projectSlug)
      if (project) {
        setTitle(project.name)
      }
    })()
  }, [projectSlug, slug])

  return (
    <Tracking pageId={projectSlug} pageType={"project" as PageType}>
      <PageMeta
        meta={{
          title: `${title} overview`,
          description: `Dive into ${title}'s key fundamental metrics and uncover trends to make better investing and operational decisions.`,
          image: `https://tokenterminal.com/api/og/project?project=${projectSlug}`,
        }}
      />
      <ProjectDatasetSchema data={data} />
      <ProjectOrganizationSchema data={data} />
      <ProjectDetailPage
        slug={projectSlug}
        projectData={data}
        activeTab="overview"
      >
        <OverviewTab type="project" projectData={data} />
      </ProjectDetailPage>
    </Tracking>
  )
}

ProjectPage.getLayout = V3StaticLayout

export default ProjectPage

export const getStaticPaths: GetStaticPaths<{
  projectSlug: string
}> = async () => {
  let { data: projects } = await trpcClient.projects.getProjects.query()

  if (isRunningBuildInPreview()) {
    // Works for non pagebuilder manually defined projects
    const buildTimeStaticProjectIds =
      process.env.BUILD_TIME_STATIC_PROJECT_IDS?.split(",")?.map((id) =>
        id.trim()
      ) ?? []

    // Build only requested ids as static for preview
    projects = projects.filter(({ id }) =>
      buildTimeStaticProjectIds.includes(id)
    )
  }

  const paths = projects.map((project) => ({
    params: {
      projectSlug: project.slug,
    },
  }))

  return {
    paths,
    fallback: "blocking",
  }
}

export const getStaticProps: GetStaticProps<
  Props,
  { projectSlug: string }
> = async ({ params }) => {
  if (!params?.projectSlug) {
    return {
      props: {},
      notFound: true,
    }
  }

  try {
    const data = await trpcClient.projects.getProject.query({
      slug: params.projectSlug,
    })
    return {
      props: {
        name: data.name,
        data,
        projectSlug: params.projectSlug,
      },
    }
  } catch (err) {
    return {
      props: {},
      notFound: true,
    }
  }
}
